import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid2 as Grid, Skeleton, Typography } from '@mui/material';
import { FIRM_ADMIN_VALUE } from 'common/constants/user';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useLayoutContext } from 'context/LayoutContext';
import { useGetSPMetadata, useSaveIdPMetadataUrl } from 'pages/Firms/hooks';
import { ServiceProviderInformation } from './components';
import { ResponsiveTextField } from './SingleSignOn.styles';

const SingleSignOn = () => {
  const [{ firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { updatePageActions } = useLayoutContext();
  const [metadataUrl, setMetadataUrl] = useState('');
  const { errors, saveIdPMetadataUrl } = useSaveIdPMetadataUrl();

  const firmId = useMemo(() => firmInfo?.id ?? null, [firmInfo]);
  const userFirmRole = useMemo(() => {
    if (!user || !firmInfo) {
      return null;
    }
    return (user.firms_permissions ?? []).find(permission => permission.name === firmInfo.name)?.user_firm_role ?? null;
  }, [user, firmInfo]);

  const isAdminOrStaffOrSuperuser = useMemo(
    () => user?.is_superuser || user?.is_staff || userFirmRole === FIRM_ADMIN_VALUE,
    [user, userFirmRole]
  );

  const saveMetadataUrl = useCallback(async () => {
    if (firmId && metadataUrl.trim().length > 0 && updatePageActions) {
      // disable save button while saving
      updatePageActions({
        mainAction: {
          id: 'firm-single-sign-on-save',
          label: 'Save',
          isActive: true, // for some reason this is inverted (IMHO)
          callback: () => {},
        },
      });
      await saveIdPMetadataUrl(metadataUrl, firmId);
    }
  }, [firmId, metadataUrl, saveIdPMetadataUrl, updatePageActions]);

  useEffect(() => {
    if (firmId && updatePageActions && isAdminOrStaffOrSuperuser) {
      updatePageActions({
        mainAction: {
          id: 'firm-single-sign-on-save',
          label: 'Save',
          isActive: metadataUrl.trim().length === 0,
          callback: saveMetadataUrl,
        },
      });
    }
  }, [firmId, metadataUrl, updatePageActions, saveMetadataUrl, isAdminOrStaffOrSuperuser]);

  const { serviceProviderMetadata } = useGetSPMetadata(firmId);

  if (!serviceProviderMetadata) {
    return <Skeleton variant="rectangular" width="100%" height={200} />;
  }

  return (
    <Grid container flexDirection="column" rowSpacing={4}>
      <Grid>
        <Typography variant="h6" gutterBottom>
          Single Sign-On Settings
        </Typography>
      </Grid>
      <Grid>
        <Typography variant="body1" gutterBottom>
          Enter your Identity Provider metadata URL to configure single sign-on.
        </Typography>
        <ResponsiveTextField
          required
          fullWidth
          id="metadata-url"
          type="url"
          label="Metadata URL"
          variant="outlined"
          size="small"
          value={metadataUrl}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setMetadataUrl(event.target.value);
          }}
          error={Boolean(errors?.length)}
          helperText={errors ? errors.join('. ') : ''}
        />
      </Grid>
      <Grid>
        <ServiceProviderInformation
          acsUrl={serviceProviderMetadata.acs_url!}
          spEntityId={serviceProviderMetadata.sp_entity_id!}
        />
      </Grid>
    </Grid>
  );
};

export default SingleSignOn;
